import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import type { SwitchMarker } from "./utils";

let instanceCounter = 0;

interface VariantSwitchMarkerLayerProps {
  markers: SwitchMarker[];
  xAxisMap?: Record<string, { scale: (v: string) => number | undefined; y: number }>;
  yAxisMap?: Record<string, unknown>;
  offset?: { top: number; bottom: number; left: number; right: number };
  onViewTest?: (testName: string) => void;
}

export function VariantSwitchMarkerLayer({
  markers,
  xAxisMap,
  onViewTest,
}: VariantSwitchMarkerLayerProps) {
  const [instanceId] = useState(() => `vml-${++instanceCounter}`);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const markerRefs = useRef<Record<number, SVGGElement | null>>({});
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!xAxisMap || markers.length === 0) return null;

  const xAxis = Object.values(xAxisMap)[0];
  if (!xAxis || !xAxis.scale) return null;

  const scale = xAxis.scale;
  const axisY = xAxis.y;
  const imgSize = 26;
  const r = 4;
  const arrowTipY = axisY + 2;
  const arrowBaseY = arrowTipY + 6;
  const imgTop = arrowBaseY;

  const showTooltip = (idx: number) => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setHoveredIdx(idx);
    const el = markerRefs.current[idx];
    if (el) {
      const rect = el.getBoundingClientRect();
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    }
  };

  const scheduleHide = () => {
    hideTimeout.current = setTimeout(() => {
      setHoveredIdx(null);
      setTooltipPos(null);
    }, 150);
  };

  const cancelHide = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
  };

  return (
    <>
      <g>
        {markers.map((marker, idx) => {
          const cx = scale(marker.date);
          if (cx === undefined || isNaN(cx)) return null;
          const imgLeft = cx - imgSize / 2;

          return (
            <g
              key={`${marker.date}-${marker.testName}`}
              ref={(el) => { markerRefs.current[idx] = el; }}
              onMouseEnter={() => showTooltip(idx)}
              onMouseLeave={scheduleHide}
              style={{ cursor: "pointer" }}
            >
              <polygon
                points={`${cx},${arrowTipY} ${cx - 4},${arrowBaseY} ${cx + 4},${arrowBaseY}`}
                fill="hsl(var(--border))"
              />
              <defs>
                <clipPath id={`${instanceId}-clip-${idx}`}>
                  <rect x={imgLeft} y={imgTop} width={imgSize} height={imgSize} rx={r} ry={r} />
                </clipPath>
              </defs>
              <rect
                x={imgLeft - 1}
                y={imgTop - 1}
                width={imgSize + 2}
                height={imgSize + 2}
                rx={r + 0.5}
                ry={r + 0.5}
                fill="hsl(var(--border))"
              />
              <rect
                x={imgLeft}
                y={imgTop}
                width={imgSize}
                height={imgSize}
                rx={r}
                ry={r}
                fill="hsl(var(--background))"
              />
              <image
                href={marker.imageUrl}
                x={imgLeft}
                y={imgTop}
                width={imgSize}
                height={imgSize}
                clipPath={`url(#${instanceId}-clip-${idx})`}
                preserveAspectRatio="xMidYMid slice"
              />
            </g>
          );
        })}
      </g>

      {hoveredIdx !== null && tooltipPos !== null && createPortal(
        <div
          ref={tooltipRef}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          style={{
            position: "fixed",
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: "translate(-50%, -100%) translateY(-8px)",
            zIndex: 99999,
            pointerEvents: "auto",
          }}
        >
          <div
            style={{
              background: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: 8,
              padding: "12px 14px",
              fontSize: 13,
              lineHeight: 1.45,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              color: "hsl(var(--foreground))",
              textAlign: "left",
              width: 260,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <img
                src={markers[hoveredIdx].imageUrl}
                alt={markers[hoveredIdx].productName}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  objectFit: "cover",
                  flexShrink: 0,
                  border: "1px solid hsl(var(--border))",
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {markers[hoveredIdx].productName}
                </div>
                <a
                  href={`https://www.amazon.com/dp/${markers[hoveredIdx].asin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "hsl(var(--primary))", fontSize: 12, textDecoration: "none" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = "none"; }}
                >
                  {markers[hoveredIdx].asin}
                </a>
              </div>
            </div>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              Variant B submitted{" "}
              {new Date(markers[hoveredIdx].switchDate).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
            <div style={{ color: "hsl(var(--muted-foreground))", fontSize: 12, marginBottom: 6 }}>
              Updates take 48–72 hours to take effect.
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewTest?.(markers[hoveredIdx!].testName);
                setHoveredIdx(null);
                setTooltipPos(null);
              }}
              style={{
                color: "hsl(var(--primary))",
                fontSize: 13,
                fontWeight: 500,
                textDecoration: "none",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.textDecoration = "none"; }}
            >
              View Test →
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
